import { ArrowRight, Phone, MessageSquare, ShieldCheck } from "lucide-react";
import { BRAND } from "@/lib/brand";

const stats = [
    { k: "5", suffix: "YRS", v: "Hands-on experience" },
    { k: "60", suffix: "MIN", v: "Avg. arrival time" },
    { k: "12", suffix: "MO", v: "Parts & labor warranty" },
    { k: "★", suffix: "", v: "Certified mechanics" },
];

export default function Hero() {
    const scrollTo = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="top"
            data-testid="hero-section"
            className="border-b hairline"
        >
            <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                <div className="col-span-12 lg:col-span-7 px-6 md:px-12 py-14 md:py-24 border-r hairline relative">
                    <div className="font-mono-tech text-[11px] text-[#1d4ed8] mb-8 flex items-center gap-3 flex-wrap">
                        <span className="inline-block w-2 h-2 bg-[#1d4ed8]" />
                        QUISEO AUTO REPAIR LLC · DENVER METRO
                        <span
                            data-testid="hero-black-owned-badge"
                            className="ml-1 inline-flex items-center gap-1.5 bg-[#0a0a0a] text-white px-2.5 py-1 font-mono-tech text-[10px] tracking-[0.16em]"
                        >
                            <span className="w-1.5 h-1.5 bg-[#1d4ed8] rounded-full" />
                            PROUDLY BLACK-OWNED
                        </span>
                    </div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-[44px] sm:text-6xl md:text-7xl lg:text-[84px] leading-[0.92] tracking-tighter text-[#0a0a0a] rise"
                    >
                        Honest repairs.
                        <br />
                        Done at your
                        <br />
                        <span className="text-[#1d4ed8]">driveway.</span>
                    </h1>

                    <p className="mt-8 max-w-xl text-[#52525b] text-base md:text-lg leading-relaxed">
                        {BRAND.legal} is a fully-mobile mechanic service offering
                        oil changes, brakes, tune-ups, pre-purchase inspections
                        and more — wherever your vehicle is parked. Transparent
                        pricing, certified work, no tow trucks.
                    </p>

                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <button
                            data-testid="hero-book-button"
                            onClick={() => scrollTo("book")}
                            className="btn-primary px-6 py-4 font-mono-tech text-[12px] inline-flex items-center gap-2"
                        >
                            Request Service <ArrowRight className="w-4 h-4" />
                        </button>
                        <a
                            data-testid="hero-call-button"
                            href={BRAND.phoneHref}
                            className="btn-ghost px-6 py-4 font-mono-tech text-[12px] inline-flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" /> Call
                        </a>
                        <a
                            data-testid="hero-text-button"
                            href={BRAND.smsHref}
                            className="btn-ghost px-6 py-4 font-mono-tech text-[12px] inline-flex items-center gap-2"
                        >
                            <MessageSquare className="w-4 h-4" /> Text
                        </a>
                    </div>
                    <div className="mt-3 font-mono-tech text-[10px] text-[#52525b]">
                        Tap to call or text: <span className="text-[#0a0a0a]">{BRAND.phone}</span>
                    </div>

                    <div className="mt-6 inline-flex items-center gap-2 font-mono-tech text-[10px] text-[#52525b]">
                        <ShieldCheck className="w-4 h-4 text-[#1d4ed8]" />
                        Licensed & insured · Certified mechanics
                    </div>

                    <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t border-l hairline">
                        {stats.map((s) => (
                            <div
                                key={s.v}
                                data-testid={`hero-stat-${s.v}`}
                                className="border-r border-b hairline p-5"
                            >
                                <div className="font-display text-3xl md:text-4xl flex items-baseline gap-1.5">
                                    <span>{s.k}</span>
                                    {s.suffix && (
                                        <span className="text-[11px] tracking-[0.18em] text-[#52525b] font-mono-tech">
                                            {s.suffix}
                                        </span>
                                    )}
                                </div>
                                <div className="font-mono-tech text-[10px] text-[#52525b] mt-2 leading-tight">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 relative bg-[#fdfdfd]">
                    <div className="h-full min-h-[340px] md:min-h-[520px] lg:min-h-full relative overflow-hidden">
                        <img
                            src="/images/hero-mechanic.png"
                            alt="Certified mobile auto mechanic working under the hood"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/30 via-transparent to-[#0a0a0a]/60" />

                        <div className="absolute top-6 left-6 right-6 flex items-start justify-between gap-3">
                            <div className="bg-white px-3 py-2 border border-[#0a0a0a]/10 shadow-[0_2px_0_0_rgba(10,10,10,0.08)]">
                                <img
                                    src={BRAND.logo}
                                    alt={BRAND.legal}
                                    className="h-10 w-auto object-contain mix-blend-multiply"
                                />
                            </div>
                            <div className="font-mono-tech text-[10px] text-white bg-[#1d4ed8] px-3 py-2 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                ON CALL
                            </div>
                        </div>

                        <div className="absolute bottom-0 left-0 right-0 bg-[#0a0a0a] text-white px-6 py-5 flex items-center justify-between">
                            <div>
                                <div className="font-mono-tech text-[10px] text-[#a1a1aa]">
                                    Now serving
                                </div>
                                <div className="font-display text-lg mt-1">
                                    Denver Metro
                                </div>
                            </div>
                            <button
                                onClick={() => scrollTo("coverage")}
                                className="font-mono-tech text-[10px] text-[#60a5fa] hover:text-white inline-flex items-center gap-1.5 transition-colors"
                            >
                                Coverage map
                                <ArrowRight className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
